/**
 * Native Payment Button Component
 * 
 * Handles In-App Purchase flow for iOS and Android
 * Uses RevenueCat for subscription management
 */

import React, { useState } from 'react';
import { Button } from './ui/button';
import { Loader2, Apple, Smartphone, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { isIOS, isAndroid } from '../services/platformService';
import { 
  purchaseSubscription, 
  purchasePackageByIdentifier,
  restorePurchases,
  isRevenueCatReady,
  PACKAGE_IDS,
  CREDIT_PACKAGE_IDS
} from '../services/revenueCatService';
import { usePaymentPlatform } from '../contexts/PaymentPlatformContext';

interface NativePaymentButtonProps {
  tier: 'standard' | 'premium' | 'ultimate' | 'provider';
  billingCycle: 'monthly' | 'annual';
  productType?: 'subscription' | 'credits';
  creditPackage?: 'starter' | 'basic' | 'premium' | 'ultimate';
  label?: string;
  variant?: 'default' | 'outline' | 'secondary';
  className?: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const NativePaymentButton: React.FC<NativePaymentButtonProps> = ({
  tier,
  billingCycle,
  productType = 'subscription',
  creditPackage,
  label,
  variant = 'default',
  className = '',
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation();
  const { refreshSubscriptionStatus, isInitialized } = usePaymentPlatform();
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);

  const handlePurchase = async () => {
    if (!isRevenueCatReady()) {
      toast.error(t('subscription.nativePaymentNotReady', { defaultValue: 'Payment system not ready. Please try again.' }));
      return;
    }

    try {
      setIsPurchasing(true);
      
      let result;
      
      if (productType === 'credits' && creditPackage) {
        // Purchase credits (consumable) using Package ID
        const packageId = CREDIT_PACKAGE_IDS[creditPackage];
        result = await purchasePackageByIdentifier(packageId);
      } else if (tier === 'provider') {
        // Purchase provider subscription using Package ID
        result = await purchasePackageByIdentifier(PACKAGE_IDS.provider_annual);
      } else {
        // Purchase user subscription (uses Package IDs internally)
        result = await purchaseSubscription(tier as 'standard' | 'premium' | 'ultimate', billingCycle);
      }
      
      if (result) {
        toast.success(t('subscription.purchaseSuccess', { defaultValue: 'Purchase successful!' }));
        await refreshSubscriptionStatus();
        onSuccess?.();
      }
      // If result is null, user cancelled - no action needed
    } catch (error: any) {
      console.error('Purchase failed:', error);
      const errorMessage = error.message || t('subscription.purchaseFailed', { defaultValue: 'Purchase failed. Please try again.' });
      toast.error(errorMessage);
      onError?.(error);
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleRestore = async () => {
    if (!isRevenueCatReady()) {
      toast.error(t('subscription.nativePaymentNotReady', { defaultValue: 'Payment system not ready. Please try again.' }));
      return;
    }

    try {
      setIsRestoring(true);
      
      const result = await restorePurchases();
      
      if (result) {
        const hasActiveEntitlements = Object.keys(result.entitlements.active).length > 0;
        
        if (hasActiveEntitlements) {
          toast.success(t('subscription.restoreSuccess', { defaultValue: 'Purchases restored successfully!' }));
          await refreshSubscriptionStatus();
        } else {
          toast.info(t('subscription.noSubscriptionsToRestore', { defaultValue: 'No previous subscriptions found to restore.' }));
        }
      }
    } catch (error: any) {
      console.error('Restore failed:', error);
      toast.error(t('subscription.restoreFailed', { defaultValue: 'Failed to restore purchases. Please try again.' }));
    } finally {
      setIsRestoring(false);
    }
  };

  const getButtonLabel = () => {
    if (label) return label;
    
    if (isIOS()) {
      return t('subscription.purchaseWithApple', { defaultValue: 'Subscribe with Apple' });
    }
    if (isAndroid()) {
      return t('subscription.purchaseWithGoogle', { defaultValue: 'Subscribe with Google Play' });
    }
    return t('subscription.subscribe', { defaultValue: 'Subscribe' });
  };

  const getButtonIcon = () => {
    if (isIOS()) return <Apple className="w-4 h-4 mr-2" />;
    if (isAndroid()) return <Smartphone className="w-4 h-4 mr-2" />;
    return null;
  };

  // Don't disable buttons based on isInitialized - let handlePurchase/handleRestore
  // show informative toast messages instead. This prevents all buttons from being
  // greyed out when RevenueCat fails to initialize (e.g., Android credentials issue).
  const isDisabled = isPurchasing || isRestoring;

  return (
    <div className="space-y-2 w-full">
      <Button
        onClick={handlePurchase}
        disabled={isDisabled}
        variant={variant}
        className={`w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white ${className}`}
      >
        {isPurchasing ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {t('subscription.processing', { defaultValue: 'Processing...' })}
          </>
        ) : (
          <>
            {getButtonIcon()}
            {getButtonLabel()}
          </>
        )}
      </Button>
      
      <Button
        onClick={handleRestore}
        disabled={isDisabled}
        variant="ghost"
        size="sm"
        className="w-full text-muted-foreground"
      >
        {isRestoring ? (
          <>
            <Loader2 className="w-3 h-3 mr-2 animate-spin" />
            {t('subscription.restoring', { defaultValue: 'Restoring...' })}
          </>
        ) : (
          <>
            <RefreshCw className="w-3 h-3 mr-2" />
            {t('subscription.restorePurchases', { defaultValue: 'Restore Purchases' })}
          </>
        )}
      </Button>
    </div>
  );
};
